import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CardComment,
  type Mention,
  useComments,
  useCreateComment,
  useDeleteComment,
  useMentionable,
  useUpdateComment,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import {
  Avatar,
  Button,
  ConfirmDialog,
  ErrorState,
  IconButton,
  Icons,
  LoadingState,
  MarkdownEditor,
  MenuItem,
  MenuList,
  MenuRow,
  Popover,
} from '@/shared/ui';
import { Markdown } from '@/shared/ui/Markdown';

import { useCardAbilities } from './abilities';
import styles from './Collab.module.css';

const MAX_LENGTH = 5000;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const escapeMarkdown = (value: string) => value.replace(/([\\`*_{}[\]()#+\-.!<>|~&])/g, '\\$1');

/** Whether `@handle` is in the text as a whole word (not the start of a longer login). */
function hasHandle(body: string, handle: string): boolean {
  return new RegExp(`@${escapeRegExp(handle)}(?![A-Za-z0-9_.-])`).test(body);
}

/**
 * Mentions are stored by user id; the text carries `@login` (unique, readable). An id is sent
 * only while its `@login` is still in the text, so deleting the text drops the mention, and a
 * typed `@name` that was never picked from the list verifies nobody.
 */
function activeMentions(body: string, chosen: Map<string, string>): string[] {
  return [...chosen].filter(([, handle]) => hasHandle(body, handle)).map(([id]) => id);
}

/**
 * Ids to keep when a stored comment is edited: those whose `@login` is still there, plus those
 * whose token was already absent before the edit (the person renamed their login since; the
 * stored identity is the id and must not be lost by an unrelated edit).
 */
function keptMentions(original: CardComment, edited: string): string[] {
  return original.mentions
    .filter((m) => hasHandle(edited, m.login) || !hasHandle(original.body, m.login))
    .map((m) => m.user_id);
}

/** Readable mentions: the account's current display name instead of its login. */
function withNames(body: string, mentions: readonly Mention[]): string {
  return mentions.reduce(
    (text, m) =>
      text.replace(
        new RegExp(`@${escapeRegExp(m.login)}(?![A-Za-z0-9_.-])`, 'g'),
        () => `**@${escapeMarkdown(m.display_name)}**`,
      ),
    body,
  );
}

/** A top-level comment with its replies (chronological), in the order of the top-level ones. */
interface Thread {
  root: CardComment;
  replies: CardComment[];
}

function groupThreads(comments: readonly CardComment[]): Thread[] {
  const threads = new Map<string, Thread>();
  const orphans: CardComment[] = [];
  for (const item of comments) {
    if (!item.thread_root_id) threads.set(item.id, { root: item, replies: [] });
  }
  for (const item of comments) {
    if (!item.thread_root_id) continue;
    const thread = threads.get(item.thread_root_id);
    if (thread) thread.replies.push(item);
    else orphans.push(item); // the root is not in the (capped) list: show it as a top-level one
  }
  return [...threads.values(), ...orphans.map((root) => ({ root, replies: [] as CardComment[] }))];
}

export function CommentsPanel({ cardId }: { cardId: string }) {
  const { t } = useTranslation();
  const viewer = useViewer();
  const { comment } = useCardAbilities();
  const comments = useComments(cardId);
  const create = useCreateComment(cardId);
  const mentionable = useMentionable(cardId, comment);
  const [body, setBody] = useState('');
  const [chosen, setChosen] = useState<Map<string, string>>(new Map());
  const options = useMemo(
    () =>
      (mentionable.data ?? [])
        .filter((p) => viewer.status !== 'authenticated' || p.user_id !== viewer.user.id)
        .map((p) => ({ id: p.user_id, name: p.display_name, handle: p.login })),
    [mentionable.data, viewer],
  );
  const threads = useMemo(() => groupThreads(comments.data ?? []), [comments.data]);

  if (comments.isPending) return <LoadingState />;
  if (comments.isError) {
    return <ErrorState error={comments.error} onRetry={() => void comments.refetch()} />;
  }

  const submit = () => {
    const text = body.trim();
    if (!text) return;
    create.mutate(
      { body: text, mention_user_ids: activeMentions(text, chosen) },
      {
        onSuccess: () => {
          setBody('');
          setChosen(new Map());
        },
      },
    );
  };

  return (
    <section className={styles.comments} aria-label={t('comments.title')}>
      {threads.length === 0 ? (
        <p className={styles.empty}>{t('comments.empty')}</p>
      ) : (
        <ol className={styles.commentList}>
          {threads.map((thread) => (
            <li key={thread.root.id} className={styles.thread} data-testid="comment-thread">
              <CommentItem
                cardId={cardId}
                item={thread.root}
                canReply={comment}
                options={options}
              />
              {thread.replies.length > 0 && (
                <ol className={styles.replies} aria-label={t('comments.replies')}>
                  {thread.replies.map((reply) => (
                    <li key={reply.id} data-testid="comment-reply">
                      <CommentItem
                        cardId={cardId}
                        item={reply}
                        canReply={comment}
                        options={options}
                      />
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ol>
      )}

      {comment ? (
        <div className={styles.composer}>
          <MarkdownEditor
            label={t('comments.newLabel')}
            value={body}
            onChange={setBody}
            maxLength={MAX_LENGTH}
            rows={3}
            mentions={options}
            onMention={(option) => {
              setChosen((current) => new Map(current).set(option.id, option.handle));
            }}
          />
          <FormError error={create.error} />
          <div className={styles.composerTools}>
            <span className={styles.hint}>{t('comments.mentionHint')}</span>
            <Button
              variant="primary"
              size="sm"
              disabled={create.isPending || !body.trim()}
              onClick={submit}
            >
              {t('comments.send')}
            </Button>
          </div>
        </div>
      ) : (
        <p className={styles.empty}>{t('comments.readOnly')}</p>
      )}
    </section>
  );
}

interface MentionOption {
  id: string;
  name: string;
  handle: string;
}

/**
 * One comment: author and time, the edit icon and the overflow menu (delete) in the top-right
 * corner, the text, then a quiet Reply action. A reply names the person it answers; a deleted
 * comment that still has replies shows as a placeholder.
 */
function CommentItem({
  cardId,
  item,
  canReply,
  options,
}: {
  cardId: string;
  item: CardComment;
  canReply: boolean;
  options: readonly MentionOption[];
}) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const update = useUpdateComment(cardId);
  const create = useCreateComment(cardId);
  const remove = useDeleteComment(cardId);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [editChosen, setEditChosen] = useState<Map<string, string>>(new Map());
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyChosen, setReplyChosen] = useState<Map<string, string>>(new Map());
  const [confirming, setConfirming] = useState(false);
  const authorName = item.author?.display_name ?? t('comments.unknownAuthor');

  if (item.deleted) {
    return (
      <div className={styles.comment} data-testid="comment">
        <span className={styles.deletedMark} aria-hidden>
          <Icons.trash />
        </span>
        <p className={styles.deletedText}>{t('comments.deletedPlaceholder')}</p>
      </div>
    );
  }

  const sendReply = () => {
    const text = replyBody.trim();
    if (!text) return;
    create.mutate(
      {
        body: text,
        mention_user_ids: activeMentions(text, replyChosen),
        reply_to_comment_id: item.id,
      },
      {
        onSuccess: () => {
          setReplying(false);
          setReplyBody('');
          setReplyChosen(new Map());
        },
      },
    );
  };

  return (
    <div className={styles.comment} data-testid="comment">
      <Avatar id={item.author?.user_id ?? item.id} name={authorName} />
      <div className={styles.commentBody}>
        <div className={styles.commentHead}>
          <span className={styles.author}>{authorName}</span>
          {item.reply_to && (
            <span className={styles.replyTo}>
              <Icons.arrowRight /> {item.reply_to.display_name}
            </span>
          )}
          <span className={styles.when}>
            {formatDateTime(item.created_at, i18n.language, zone)}
            {item.edited_at &&
              ` · ${t('comments.edited', {
                when: formatDateTime(item.edited_at, i18n.language, zone),
              })}`}
          </span>
          {!editing && (item.can_edit || item.can_delete) && (
            <span className={styles.commentTools}>
              {item.can_edit && (
                <IconButton
                  label={t('comments.edit')}
                  icon={<Icons.edit />}
                  onClick={() => {
                    setEditing(true);
                    setEditBody(item.body);
                    setEditChosen(new Map());
                  }}
                />
              )}
              {item.can_delete && (
                <Popover
                  label={t('comments.actions')}
                  width={200}
                  alignEnd
                  trigger={(props) => (
                    <IconButton label={t('comments.actions')} icon={<Icons.more />} {...props} />
                  )}
                >
                  {(close) => (
                    <MenuList>
                      <MenuRow>
                        <MenuItem
                          danger
                          icon={<Icons.trash />}
                          onClick={() => {
                            close();
                            setConfirming(true);
                          }}
                        >
                          {t('comments.delete')}
                        </MenuItem>
                      </MenuRow>
                    </MenuList>
                  )}
                </Popover>
              )}
            </span>
          )}
        </div>
        {editing ? (
          <div className={styles.composer}>
            <MarkdownEditor
              label={t('comments.editLabel')}
              value={editBody}
              onChange={setEditBody}
              maxLength={MAX_LENGTH}
              rows={4}
              autoFocus
              mentions={options}
              onMention={(option) => {
                setEditChosen((current) => new Map(current).set(option.id, option.handle));
              }}
            />
            <FormError error={update.error} />
            <div className={styles.actions}>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(false);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={update.isPending || !editBody.trim()}
                onClick={() => {
                  update.mutate(
                    {
                      commentId: item.id,
                      body: editBody.trim(),
                      mention_user_ids: [
                        ...new Set([
                          ...keptMentions(item, editBody),
                          ...activeMentions(editBody, editChosen),
                        ]),
                      ],
                    },
                    {
                      onSuccess: () => {
                        setEditing(false);
                      },
                    },
                  );
                }}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Markdown>{withNames(item.body, item.mentions)}</Markdown>
            {canReply && !replying && (
              <button
                type="button"
                className={styles.replyAction}
                onClick={() => {
                  setReplying(true);
                }}
              >
                {t('comments.reply')}
              </button>
            )}
          </>
        )}
        {replying && (
          <div className={styles.composer}>
            <MarkdownEditor
              label={t('comments.replyLabel', { name: authorName })}
              value={replyBody}
              onChange={setReplyBody}
              maxLength={MAX_LENGTH}
              rows={3}
              autoFocus
              mentions={options}
              onMention={(option) => {
                setReplyChosen((current) => new Map(current).set(option.id, option.handle));
              }}
            />
            <FormError error={create.error} />
            <div className={styles.actions}>
              <Button
                size="sm"
                onClick={() => {
                  setReplying(false);
                  setReplyBody('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={create.isPending || !replyBody.trim()}
                onClick={sendReply}
              >
                {t('comments.send')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {confirming && (
        <ConfirmDialog
          title={t('comments.deleteTitle')}
          message={t('comments.deleteMessage')}
          confirmLabel={t('common.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            remove.mutate(item.id, {
              onSettled: () => {
                setConfirming(false);
              },
            });
          }}
        />
      )}
    </div>
  );
}
